import { Button, Descriptions, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type FinderVehicleManagementModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/FinderVehicleManagementModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import FinderVehicleManagementModuleAction from './FinderVehicleManagementModuleAction';
import FinderVehicleManagementModuleDetailsInner from './FinderVehicleManagementModuleDetailsInner';

export type FinderVehicleManagementModulePageProps = {
    module: FinderVehicleManagementModuleWithPermissionsSpecsFragment;
};

const FinderVehicleManagementModulePage = ({ module }: FinderVehicleManagementModulePageProps) => {
    const { t } = useTranslation(['finderVehicleManagementModuleDetails', 'common']);
    const navigate = useNavigate();

    const screens = Grid.useBreakpoint();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const permissions = {
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('finderVehicleManagementModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('finderVehicleManagementModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={[<FinderVehicleManagementModuleAction module={module} permissions={permissions} />]}
            footer={[
                <Button form="updateMainDetails" htmlType="submit" type="primary">
                    {t('finderVehicleManagementModuleDetails:actions.update')}
                </Button>,
            ]}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md
                    ? t('finderVehicleManagementModuleDetails:title', { name: module.displayName })
                    : module.displayName
            }
        >
            <FinderVehicleManagementModuleDetailsInner module={module} />
        </ConsolePageWithHeader>
    );
};

export default FinderVehicleManagementModulePage;
